import {
  ExpandedLinkElement,
  ImageElement,
  LinkElement,
  ParagraphElement,
  Routes,
  SlateElement,
  SlateNode,
  UploadInProgressElement,
} from '@all-hands-hq/plex-graphql-schema';
import assertNever from 'assert-never';
import imageExtensions from 'image-extensions';
import isUrl from 'is-url';
import { Editor, Node, Path, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { client } from '../../../../async/utils/client';
import {
  LinkPreviewMetadataFragment,
  UploadFileMutation,
  UploadFileMutationVariables,
} from '../../../../graphql/gen/types';
import { UPLOAD_FILE_MUTATION } from '../../../../graphql/mutations/UploadFile';
import { ReactEditorWithEntities } from './useSlate';
import { getVoidElementInsertLocation, insertAndSelectElements, isContinuousInline, updateElements } from './utils';

type SavedFile = {
  originalFile: string;
  assetLocation: string;
};

export function withFiles<T extends ReactEditorWithEntities>(editor: T): T {
  const { insertData } = editor;

  editor.insertData = (data): void => {
    const text = data.getData('text/plain');
    const { files } = data;

    if (files && files.length > 0) {
      insertFiles(editor, files);
    } else if (text && isImageUrl(text)) {
      insertImage(editor, text);
    } else {
      insertData(data);
    }
  };

  return editor;
}

export function insertFiles(editor: ReactEditorWithEntities, files: FileList): void {
  if (files.length > 0) {
    const placeholderNodes: UploadInProgressElement[] = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append(`file`, file, file.name);
      placeholderNodes.push({
        type: 'upload_in_progress',
        source: { type: 'file', file },
        message: `Attaching ${file.name}…`,
        children: [{ text: '' }],
      });

      const match = (n: SlateElement): n is UploadInProgressElement =>
        n.type === 'upload_in_progress' && n.source.type === 'file' && n.source.file === file;

      fetch('/api/upload', { method: 'POST', body: formData })
        .then((res) => res.json())
        .then((savedFiles: SavedFile[]) => {
          savedFiles.forEach((uploadedFile) => {
            const url = uploadedFile.assetLocation;

            if (isImageUrl(uploadedFile.assetLocation)) {
              updateElements<UploadInProgressElement, ImageElement>({
                editor,
                match,
                map: () => ({ type: 'image', url }),
              });
            } else {
              editor.setEntities((entities) => ({
                ...entities,
                expanded_link: [
                  ...entities.expanded_link,
                  {
                    __typename: 'LinkMetadata',
                    id: url,
                    details: {
                      __typename: 'AttachmentLinkMetadata',
                      url: uploadedFile.assetLocation,
                      extension: getExtension(url) || '',
                    },
                  },
                ],
              }));

              updateElements<UploadInProgressElement, ExpandedLinkElement>({
                editor,
                match,
                map: () => ({
                  type: 'expanded_link',
                  url,
                  linkText: file.name,
                }),
              });
            }
          });
        });
      // TODO: error handling
    }

    insertAndSelectElements(editor, placeholderNodes, { replaceEmptyLine: true });
  }
}

export function insertLink(editor: Editor, url: string): void {
  if (editor.selection) {
    wrapLink(editor, url);
  }
}

export function isLinkActive(editor: Editor): boolean {
  const [link] = Editor.nodes(editor, { match: (n) => n.type === 'link' });
  return !!link;
}

export function unwrapLink(editor: Editor): void {
  Transforms.unwrapNodes(editor, { match: (n) => n.type === 'link' });
}

function insertImage(editor: Editor, url: string): void {
  insertAndSelectElements(
    editor,
    [
      {
        type: 'upload_in_progress',
        message: `Fetching ${url}...`,
        source: { type: 'url', url },
        children: [{ text: '' }],
      },
    ],
    { replaceEmptyLine: true }
  );

  client
    .mutate<UploadFileMutation, UploadFileMutationVariables>({
      mutation: UPLOAD_FILE_MUTATION,
      variables: { url },
    })
    .then((response) => {
      if (response.data?.uploadFile != null) {
        const { plexAssetUrl } = response.data?.uploadFile;

        const match = (n: SlateElement): n is UploadInProgressElement =>
          n.type === 'upload_in_progress' && n.source.type === 'url' && n.source.url === url;

        updateElements<UploadInProgressElement, ImageElement>({
          editor,
          match,
          map: () => ({ type: 'image', url: plexAssetUrl }),
        });
      }
    });
  //
}

function getExtension(url: string): string | undefined {
  return Routes.extractUrlExtension(new URL(url));
}

function isImageUrl(url: string): boolean {
  if (!url) return false;
  if (!isUrl(url)) return false;
  try {
    const ext = getExtension(url);
    return ext != null && imageExtensions.includes(ext);
  } catch (_) {
    return false;
  }
}

function wrapLink(editor: Editor, url: string): void {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = !selection || Range.isCollapsed(selection);
  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
}

export function getLinkElementAbove({
  selection,
  editor,
}: {
  selection: Range;
  editor: Editor;
}): {
  linkElementPath: Path;
  linkElement: LinkElement;
  url: string;
  text: string;
} | null {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const existingLinkElementEntry: NodeEntry<LinkElement> | undefined = Editor.above<LinkElement>(editor, {
    at: selection,
    match: (n: SlateNode) => n.type === 'link',
  });

  if (existingLinkElementEntry != null) {
    const [linkElement, linkElementPath] = existingLinkElementEntry;

    return {
      linkElementPath,
      linkElement,
      url: linkElement.url,
      text: linkElement.children[0].text || '',
    };
  } else {
    return null;
  }
}

export function insertLinkElement(
  editor: ReactEditor,
  { selection, url, text }: { selection: Range; url: string; text: string }
): void {
  const selectionStart = Range.start(selection);
  const linkElementPathBeforeNormalization =
    selectionStart.offset === 0 ? selectionStart.path : Path.next(selectionStart.path);

  if (Range.isCollapsed(selection)) {
    Editor.withoutNormalizing(editor, () => {
      Transforms.insertNodes(editor, [{ type: 'link', url, children: [{ text }] }], {
        at: selection.anchor,
      });
      Transforms.select(editor, linkElementPathBeforeNormalization);
    });
  } else {
    Editor.withoutNormalizing(editor, () => {
      Transforms.wrapNodes(editor, { type: 'link', children: [] }, { at: selection, split: true });
      updateLinkElement(editor, linkElementPathBeforeNormalization, { url, text });
    });
  }
}

export function updateLinkElement(
  editor: ReactEditor,
  linkElementPath: Path,
  { url, text }: { url: string; text: string }
): void {
  Editor.withoutNormalizing(editor, () => {
    Transforms.setNodes(editor, { url }, { at: linkElementPath });
    Transforms.insertText(editor, text, { at: linkElementPath });
    Transforms.select(editor, linkElementPath);
  });
}

export function unlinkLinkElement(editor: ReactEditor, linkElementPath: Path): void {
  Editor.withoutNormalizing(editor, () => {
    Transforms.select(editor, linkElementPath);
    Transforms.unwrapNodes(editor, { at: linkElementPath });
  });
}

export function canLinkifySelection(editor: Editor, range: Range): boolean {
  return (
    isContinuousInline(editor, range) &&
    Editor.above(editor, { at: range, match: (n) => Editor.isVoid(editor, n) }) == null
  );
}

/**
 * Transform link from inline to block
 */
export function expandLink(
  editor: ReactEditorWithEntities,
  node: LinkElement,
  path: Path,
  linkMetadataDetails: LinkPreviewMetadataFragment['details'] &
    (
      | { __typename: 'PlexPostLinkMetadata' }
      | { __typename: 'PlexFactLinkMetadata' }
      | { __typename: 'AttachmentLinkMetadata' }
      | { __typename: 'ImageLinkMetadata' }
      | { __typename: 'GoogleDocLinkMetadata' }
      | { __typename: 'GoogleDriveFileLinkMetadata' }
      | { __typename: 'GoogleSheetsLinkMetadata' }
      | { __typename: 'GoogleSlidesLinkMetadata' }
      | { __typename: 'GenericLink' }
    )
): void {
  const linkText = Editor.string(editor, path);

  Editor.withoutNormalizing(editor, () => {
    let block: ImageElement | ExpandedLinkElement;

    switch (linkMetadataDetails.__typename) {
      case 'AttachmentLinkMetadata':
      case 'GenericLink':
      case 'GoogleDocLinkMetadata':
      case 'GoogleDriveFileLinkMetadata':
      case 'GoogleSheetsLinkMetadata':
      case 'GoogleSlidesLinkMetadata':
      case 'PlexPostLinkMetadata':
      case 'PlexFactLinkMetadata': {
        editor.setEntities((entities) => ({
          ...entities,
          expanded_link: [
            ...entities.expanded_link,
            { __typename: 'LinkMetadata', id: node.url, details: linkMetadataDetails },
          ],
        }));
        block = {
          type: 'expanded_link',
          url: node.url,
          linkText,
          children: [{ text: '' }],
        };
        break;
      }
      case 'ImageLinkMetadata': {
        block = {
          type: 'image',
          url: node.url,
          children: [{ text: '' }],
        };
        break;
      }
      default: {
        assertNever(linkMetadataDetails);
      }
    }

    Transforms.removeNodes(editor, { at: path });
    const location = getVoidElementInsertLocation(editor, {
      startingAt: path,
      replaceEmptyLine: true,
    });
    Transforms.insertNodes(editor, [(block as unknown) as Node], {
      at: location,
      select: true,
    });
  });
}

/**
 * Transform link from block to inline
 */
export function inlineNodeAsLink(editor: ReactEditor, node: ExpandedLinkElement | ImageElement, path: Path): void {
  const { url } = node;

  const linkText = node.type === 'expanded_link' && node.linkText != null ? node.linkText : url;

  const inlineLink: ParagraphElement = {
    type: 'paragraph',
    children: [{ type: 'link', url, children: [{ text: linkText }] }],
  };

  Editor.withoutNormalizing(editor, () => {
    Transforms.removeNodes(editor, { at: path });
    Transforms.insertNodes(editor, [(inlineLink as unknown) as Node], { at: path, select: true });
  });
}
